/*
 * Industry landing page content — `fr-be` locale (Belgium market).
 *
 * Belgian French (français de Belgique) translation of the Belgium/base variant
 * of every industry. Uses the BELGIUM copy for all fields (the international
 * wording belongs to the `en` set). All Belgium-specific pains/features are
 * retained because the Belgian market sees them. B2B register, vouvoiement.
 *
 * WARNING: the legally-sensitive `compliance` copy is PENDING NATIVE LEGAL
 * REVIEW (G6 / Story 4.10). Dates, rates, thresholds and penalties are carried
 * over faithfully from the source but must be verified by a native legal reviewer.
 *
 * Pure data / zero-import (NFR10) so the build config can import the slug list.
 */

import type { Industry } from "./types";

export const INDUSTRIES_FR_BE: Industry[] = [
  {
    slug: "construction",
    name: "Construction",
    headline:
      "Enregistrement du temps pour les équipes de chantier — check-in vérifié par géolocalisation, heures de projet et checkin@work, dans une seule app",
    intro:
      "Des équipes dispersées sur les chantiers, des heures griffonnées sur papier, la conformité des sous-traitants comme préoccupation permanente. Done-it dote chaque chantier d'un enregistrement du temps numérique étanche qui satisfait l'inspecteur et alimente votre traitement salarial en un clic.",
    pains: [
      {
        title: "Aucune vue fiable de qui a travaillé où",
        body: "Avec plusieurs chantiers actifs et des équipes changeantes, les relevés d'heures manuels sont par définition inexacts. Les travailleurs arrondissent, oublient des enregistrements ou les complètent des jours plus tard — ce qui vous laisse avec des données salariales que vous ne pouvez pas justifier.",
      },
      {
        title: "Les sous-traitants sont aussi votre responsabilité",
        body: "La loi belge prévoit une responsabilité solidaire : si un sous-traitant n'enregistre pas ses travailleurs (Dimona) ou ne respecte pas CheckIn@Work, c'est vous, en tant qu'entrepreneur principal, qui portez le risque d'inspection. Sans une vue unique, vous avancez à l'aveugle.",
      },
      {
        title: "Les dépassements de budget se révèlent trop tard",
        body: "Au moment où vous remarquez qu'un chantier a épuisé ses heures planifiées, la marge a déjà disparu. Il n'y a pas de décompte en direct — seulement une mauvaise surprise au moment de la facturation.",
      },
      {
        title: "La préparation des salaires coûte des heures chaque mois",
        body: "Rassembler les relevés d'heures, les rapprocher des projets et les traduire pour le secrétariat social prend des jours. Chaque enregistrement est une erreur potentielle.",
      },
    ],
    features: [
      {
        title: "Check-in vérifié par géolocalisation par chantier",
        body: "Une seule pression enregistre l'arrivée du travailleur, vérifiée par géolocalisation, et rattache les heures au bon projet. Pas de lecteurs de cartes, pas de terminaux — simplement le smartphone que votre équipe a déjà en poche. Alimente check-in at work pour les chantiers belges.",
      },
      {
        title: "Heures restantes en direct par projet",
        body: "Définissez les heures planifiées comme budget lorsque vous ouvrez le projet. Les travailleurs voient un décompte de ce qu'il reste ; vous le voyez en direct dans votre tableau de bord. Les dépassements de budget sont visibles avant qu'ils ne surviennent.",
      },
      {
        title: "Gestion et présence des sous-traitants",
        body: "Ajoutez des sous-traitants à un projet, attribuez-les au check-in et suivez leurs heures aux côtés de votre propre équipe — dans la même vue d'ensemble.",
      },
      {
        title: "Synchronisation CheckIn@Work",
        body: "Done-it transmet automatiquement les données de présence vers la plateforme officielle belge CheckIn@Work. Pas de double saisie, pas de sessions manuelles sur le portail de l'ONSS.",
      },
      {
        title: "Export salarial en un clic",
        body: "Toutes les heures enregistrées — par travailleur, par projet — vous les exportez vers un tableur clair et les transmettez directement à votre secrétariat social.",
      },
    ],
    compliance: {
      label: "Check-in at work + loi de 2027",
      title: "La construction enregistre déjà la présence — et 2027 y ajoute davantage",
      body: "Sur les chantiers belges, l'arrivée de chaque travailleur doit être enregistrée via check-in at work et transmise à l'ONSS (pour les chantiers au-dessus du seuil). Aujourd'hui, il s'agit uniquement du check-in — l'arrivée. À partir du 1er janvier 2027, une obligation générale ajoute un enregistrement complet du temps de travail pour chaque employeur, y compris la construction : vous devez tenir une vue fiable de l'intégralité du temps de travail (le début et la fin de la journée ne doivent pas être transmis automatiquement à l'ONSS). Done-it couvre les deux — il alimente check-in at work et tient l'enregistrement du temps étanche qu'exigent les règles de 2027.",
      slug: "digitaal-in-en-uitchecken-verplicht-op-bouwwerven-vanaf-2025",
    },
    workflow: [
      {
        step: "L'équipe arrive sur le chantier",
        body: "Chaque travailleur tape une seule fois pour s'enregistrer. Le GPS confirme la localisation, enregistre l'heure et rattache les heures au projet. Pas de papier, pas de terminal.",
      },
      {
        step: "Vous voyez tout en direct",
        body: "Heures, budget restant, présence des sous-traitants et chaque travail supplémentaire signalé depuis le chantier — le tout dans votre tableau de bord, en temps réel.",
      },
      {
        step: "Fin de mois en quelques minutes",
        body: "Exportez toutes les heures par travailleur et par projet. Transmettez-les à votre secrétariat social. Les données de Done-it satisfont déjà à CheckIn@Work et aux exigences de 2027.",
      },
    ],
    stats: [
      { value: "1 pression", label: "pour s'enregistrer sur chaque chantier — pas de terminal, pas de papier" },
      { value: "1 clic", label: "export salarial prêt pour votre secrétariat social" },
      { value: "1 janv. 2027", label: "obligation générale d'enregistrement du temps — soyez prêt" },
    ],
    quote: {
      text: "Notre équipe s'enregistre elle-même sur le chantier. À la fin du mois, le traitement salarial est tout simplement correct — nous avons complètement arrêté de courir après les relevés d'heures.",
      who: "Gérant, entreprise de construction · 14 travailleurs",
      result: "5 heures par semaine économisées sur l'administration",
    },
    relatedSlugs: ["installation", "maintenance", "field-service"],
    relatedArticles: [
      "digitaal-in-en-uitchecken-verplicht-op-bouwwerven-vanaf-2025",
      "checkin-work-ciao-verplichtingen-fouten-en-tips-voor-kmo-s-in-belgie",
      "onderaannemers-in-schoonmaak-renovatie-en-nieuwbouw-in-belgie-de-basis-om-boetes-te-vermijden-dimona-limosa-checkin-work-ciao-en-ec3-2a",
      "flashcontroles-in-de-belgische-bouwsector-zomer-2025-hoe-vermijd-je-boetes-met-digitale-oplossingen",
    ],
  },

  {
    slug: "cleaning",
    name: "Nettoyage",
    headline:
      "Enregistrement du temps pour les entreprises de nettoyage — check-in and out at work, preuve de présence et traitement salarial dans une seule app",
    intro:
      "Check-in and out at work (CIAO@work) est obligatoire depuis le 1er septembre 2024 : enregistrement de l'entrée et de la sortie en temps réel pour chaque agent d'entretien, sur chaque site, sans saisie rétroactive autorisée. Done-it assure l'enregistrement automatiquement et le transforme en un export salarial clair.",
    pains: [
      {
        title: "Check-in and out at work est obligatoire — et fastidieux",
        body: "Chaque travailleur doit enregistrer lui-même son entrée et sa sortie en temps réel, pauses comprises — et chaque fois cela est transmis à l'ONSS, parfois plusieurs fois par jour. L'employeur ne peut pas le faire à sa place, et l'enregistrement anticipé est interdit. Un seul enregistrement manqué constitue une infraction.",
      },
      {
        title: "Les sous-traitants sont aussi votre responsabilité",
        body: "La loi belge prévoit une responsabilité solidaire : si un sous-traitant n'enregistre pas ses travailleurs (Dimona) ou ne respecte pas check-in and out at work, c'est vous, en tant qu'entrepreneur principal, qui portez le risque d'inspection. Sans une vue unique, vous avancez à l'aveugle.",
      },
      {
        title: "Prouver la présence sur chaque site",
        body: "Clients et inspecteurs attendent des enregistrements de présence étanches. Les listes manuelles peuvent être contestées ; les check-ins numériques vérifiés par géolocalisation, non.",
      },
      {
        title: "Des équipes dispersées sur de nombreux sites",
        body: "Avec des agents d'entretien qui se déplacent entre sites au fil de la journée, vous n'avez aucune vue en direct de qui travaille où — jusqu'à ce que quelqu'un appelle.",
      },
      {
        title: "Des erreurs de salaire dues aux heures arrondies",
        body: "Sans heures d'entrée et de sortie précises, le traitement salarial est reconstitué de mémoire. Les heures sont arrondies vers le haut, les coûts grimpent et les discussions suivent.",
      },
    ],
    features: [
      {
        title: "Check-in and out en temps réel",
        body: "Les travailleurs enregistrent leur entrée et leur sortie sur leur téléphone, sur chaque site, vérifiés par géolocalisation ; l'heure est exacte, avec enregistrement des pauses. (L'intégration officielle check-in and out at work / CIAO@work fait partie des Project plans.)",
        plan: "Time Basic",
      },
      {
        title: "Déduction automatique des pauses",
        body: "Les pauses obligatoires sont automatiquement déduites de la journée de travail — pas de calcul manuel, pas de discussions.",
        plan: "Time Basic",
      },
      {
        title: "Vue en direct sur plusieurs sites",
        body: "Voyez quels agents d'entretien sont sur quels sites en ce moment. Repérez les lacunes avant que les clients ne les remarquent.",
      },
      {
        title: "Enregistrement des absences avec certificats médicaux numériques",
        body: "Les travailleurs chargent les certificats médicaux et les documents d'absence directement depuis l'app. Prend en charge le chômage intempéries et d'autres types d'absence pertinents pour le secteur.",
      },
      {
        title: "Export salarial en un clic",
        body: "Les horodatages conformes circulent directement vers un export prêt pour le salaire à destination de votre secrétariat social. Pas de recopiage, pas d'erreurs.",
      },
    ],
    compliance: {
      label: "Check-in and out at work — obligatoire depuis sept. 2024",
      title: "Check-in and out at work est en vigueur — chaque entreprise de nettoyage doit s'y conformer",
      body: "Check-in and out at work (CIAO@work) est obligatoire pour le nettoyage professionnel de biens immobiliers de tiers depuis le 1er septembre 2024. Chaque travailleur — salarié, indépendant, intérimaire ou stagiaire — doit enregistrer le début et la fin du travail en temps réel, pauses comprises, chaque fois transmis à l'ONSS (parfois plusieurs fois par jour). L'obligation entre en vigueur à partir de 30.000 € (aucun sous-traitant), 5.000 € (un sous-traitant), ou tout montant en cas de deux sous-traitants ou plus. Les amendes au titre du Code pénal social vont de 300 € à 6.000 € par travailleur. Done-it synchronise automatiquement les données d'entrée et de sortie avec la plateforme officielle — pas de double saisie.",
      slug: "checkin-work-ciao-verplichtingen-fouten-en-tips-voor-kmo-s-in-belgie",
    },
    workflow: [
      {
        step: "L'agent d'entretien arrive sur le site",
        body: "Une pression enregistre son entrée, vérifiée par géolocalisation. Le début et la fin de la pause sont enregistrés dans l'app — exactement comme l'exige check-in and out at work.",
      },
      {
        step: "Les données circulent automatiquement vers l'ONSS",
        body: "Done-it transmet les données d'entrée et de sortie en temps réel vers la plateforme officielle. Vous ne devez jamais vous connecter manuellement au portail.",
      },
      {
        step: "Le traitement salarial tient en un clic",
        body: "Toutes les données de présence — avec heures exactes, sites et pauses — vous les exportez proprement pour votre secrétariat social.",
      },
    ],
    stats: [
      { value: "1er sept.", label: "2024 — check-in and out at work est devenu obligatoire" },
      { value: "300 €–6k €", label: "amende par travailleur en cas de non-respect" },
      { value: "0", label: "double saisie — Done-it synchronise automatiquement" },
    ],
    quote: {
      text: "Nous pouvons enfin prouver qui a nettoyé où et quand. Fini les listes de présence, finies les discussions avec les clients — le GPS s'en charge.",
      who: "Manager, entreprise de nettoyage · 9 travailleurs",
      result: "Plus aucun arrondi des heures vers le haut",
    },
    relatedSlugs: ["maintenance", "field-service", "construction"],
    relatedArticles: [
      "checkin-work-ciao-verplichtingen-fouten-en-tips-voor-kmo-s-in-belgie",
      "onderaannemers-in-schoonmaak-renovatie-en-nieuwbouw-in-belgie-de-basis-om-boetes-te-vermijden-dimona-limosa-checkin-work-ciao-en-ec3-2a",
      "verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedrijven-weten",
    ],
  },

  {
    slug: "installation",
    name: "Installation",
    headline:
      "Enregistrement du temps pour les équipes d'installation — heures de projet, navigation et gestion de budget dans une seule app",
    intro:
      "Envoyez vos monteurs à la bonne adresse avec tout ce dont ils ont besoin, rattachez chaque heure travaillée automatiquement au bon chantier, et voyez le budget restant avant que le chantier ne se termine. Done-it est la façon la plus simple de tenir les projets d'installation dans les délais et dans les coûts.",
    pains: [
      {
        title: "Les infos de chantier n'arrivent pas à temps sur le terrain",
        body: "Les équipes arrivent sur un chantier sans l'adresse, la personne de contact ou les détails de la mission. Ce premier coup de fil, ce sont dix minutes de temps facturable perdues avant qu'une seule heure ne soit travaillée.",
      },
      {
        title: "Les heures ne sont pas rattachées au bon projet",
        body: "Lorsque les travailleurs complètent leurs heures manuellement en fin de journée — s'ils les complètent — les heures atterrissent sur le mauvais chantier, ou disparaissent complètement. Les factures ne correspondent pas à la réalité.",
      },
      {
        title: "Les dépassements de budget sont remarqués trop tard",
        body: "Sans un décompte en direct des heures restantes par projet, vous ne découvrez qu'un chantier a débordé qu'au moment d'établir la facture — pas quand vous pouviez encore réagir.",
      },
      {
        title: "Les heures des sous-traitants difficiles à suivre",
        body: "Les sous-traitants qui travaillent sur le même chantier utilisent d'autres systèmes, complètent leurs heures séparément ou pas du tout. Le rapprochement en fin de mois est un processus lent et sujet aux erreurs.",
      },
    ],
    features: [
      {
        title: "Infos de projet partagées directement dans l'app",
        body: "Les travailleurs ouvrent le projet sur leur téléphone : adresse, heures planifiées, personne de contact et fichiers chargés (plans, instructions, PDF). Tout ce dont ils ont besoin est là avant qu'ils n'arrivent.",
        plan: "Project Basic",
      },
      {
        title: "Navigation vers le chantier en un bouton",
        body: "Tapez une seule fois et le téléphone navigue vers l'adresse du projet — intégré à l'app, sans appli de cartes séparée.",
      },
      {
        title: "Décompte des heures restantes par projet",
        body: "Définissez les heures planifiées lorsque vous ouvrez le projet. Les travailleurs voient combien d'heures il reste ; vous voyez le statut en direct dans votre tableau de bord. Les dépassements sont visibles avant qu'ils ne surviennent.",
        plan: "Project Basic",
      },
      {
        title: "Check-in des travailleurs par projet",
        body: "Chaque travailleur s'enregistre sur le bon projet. Vous savez toujours qui se trouve en ce moment sur quel chantier.",
        plan: "Project Basic",
      },
      {
        title: "Enregistrement du travail supplémentaire et du matériel",
        body: "Lorsque la mission s'étend sur le chantier, les travailleurs enregistrent le travail supplémentaire et le matériel en temps réel — avec texte et photos — pour que rien ne soit facturé de mémoire.",
        plan: "Project Basic",
      },
    ],
    compliance: {
      label: "Loi de 2027 + responsabilité solidaire",
      title: "Les équipes d'installation relèvent à la fois de la loi de 2027 et des règles sur les sous-traitants",
      body: "À partir du 1er janvier 2027, chaque employeur doit enregistrer les heures de travail avec un système numérique étanche. Pour les travaux d'installation sur les grands chantiers (≥ 500.000 € hors TVA), l'enregistrement CheckIn@Work est déjà obligatoire aujourd'hui — et avec des sous-traitants, vous portez une responsabilité solidaire pour leur conformité. Done-it couvre les deux : il fournit le système général d'enregistrement du temps qu'exige 2027 et synchronise avec CheckIn@Work là où le seuil s'applique.",
      slug: "verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedrijven-weten",
    },
    workflow: [
      {
        step: "Le travailleur ouvre le projet sur son téléphone",
        body: "Adresse, contact, mission et fichiers chargés sont là immédiatement. Ils naviguent en une pression — pas d'appels au bureau pour la route.",
      },
      {
        step: "Les heures s'enregistrent automatiquement sur le bon chantier",
        body: "Le check-in rattache chaque minute travaillée au bon projet. Le budget restant est mis à jour en direct.",
      },
      {
        step: "Le travail supplémentaire est consigné sur le chantier",
        body: "Les modifications de la mission, le matériel supplémentaire et les retards sont enregistrés avec texte et photos au moment où ils surviennent — prêts pour la facture.",
      },
    ],
    stats: [
      { value: "Temps réel", label: "vue sur les heures restantes par projet" },
      { value: "0", label: "appels au bureau pour les adresses de chantier" },
      { value: "1 janv. 2027", label: "obligation générale d'enregistrement du temps" },
    ],
    quote: {
      text: "Les heures atterrissent automatiquement sur le bon projet. Le calcul du coût des chantiers est devenu simple d'un coup — nous voyons la marge réelle avant de clôturer un chantier.",
      who: "Gérant, entreprise d'installation · 6 travailleurs",
      result: "Coût réel par chantier, en direct",
    },
    relatedSlugs: ["construction", "electricians", "hvac"],
    relatedArticles: [
      "onderaannemers-in-schoonmaak-renovatie-en-nieuwbouw-in-belgie-de-basis-om-boetes-te-vermijden-dimona-limosa-checkin-work-ciao-en-ec3-2a",
      "verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedrijven-weten",
      "digitaal-in-en-uitchecken-verplicht-op-bouwwerven-vanaf-2025",
    ],
  },

  {
    slug: "field-service",
    name: "Service extérieur",
    headline:
      "Enregistrement du temps pour les équipes de service extérieur — kilomètres, frais et heures en déplacement, le tout dans une seule app",
    intro:
      "Des techniciens qui visitent plusieurs clients par jour, des tickets dans les boîtes à gants, des kilomètres notés de mémoire. Done-it consigne les heures, les kilomètres et les frais sur le terrain — rattachés au bon chantier au moment où ils surviennent.",
    pains: [
      {
        title: "Des kilomètres notés des jours plus tard — quand ils le sont",
        body: "Demander aux techniciens de reconstituer leurs trajets en fin de semaine conduit à des indemnités inexactes, des discussions avec les clients et une pile croissante de kilomètres non introduits.",
      },
      {
        title: "Des frais sur des tickets papier qui disparaissent",
        body: "Un ticket dans une boîte à gants est un coût qui n'atterrira peut-être jamais sur une facture. Le coût réel par chantier est toujours sous-estimé.",
      },
      {
        title: "Aucune vue en temps réel de l'endroit où se trouve l'équipe",
        body: "Sans check-ins en direct, vous n'apprenez où se trouve un technicien que lorsque quelque chose tourne mal et que quelqu'un appelle.",
      },
      {
        title: "Difficile de voir la marge réelle par chantier",
        body: "Lorsque main-d'œuvre, déplacement et frais sont suivis dans des systèmes séparés — ou pas du tout — il n'y a jamais un chiffre de coût unique par chantier.",
      },
    ],
    features: [
      {
        title: "Enregistrement automatique des kilomètres (Trax-it)",
        body: "Le GPS enregistre le départ, l'arrivée et la distance exacte par trajet. Configurez des types de transport (voiture personnelle, véhicule de société, covoiturage) avec leurs propres tarifs d'indemnité. Pas de carnet de route manuel.",
        plan: "Add-on",
      },
      {
        title: "Gestion des frais sur le terrain",
        body: "Les travailleurs prennent en photo un ticket sur le terrain ; il est rattaché au chantier et prêt pour le remboursement — pas couru après en fin de mois.",
        plan: "Time Basic",
      },
      {
        title: "Gestion des coûts rattachée au projet",
        body: "Heures, kilomètres et frais sont additionnés au chantier en temps réel. Le coût par chantier est visible tant que le chantier est encore ouvert.",
        plan: "Project Basic",
      },
      {
        title: "Check-in vérifié par géolocalisation et vue d'équipe en direct",
        body: "Chaque technicien s'enregistre sur le site client. Vous voyez qui est où, en ce moment — pas d'appels, pas de devinettes.",
      },
      {
        title: "Export salarial en un clic",
        body: "Heures et indemnités kilométriques, vous les exportez proprement pour le secrétariat social — avec les tarifs kilométriques officiels belges appliqués automatiquement.",
      },
    ],
    compliance: {
      label: "Loi de 2027 + règles kilométriques",
      title: "Le service extérieur relève de l'obligation générale de 2027 — et les kilomètres ont leurs propres règles",
      body: "À partir du 1er janvier 2027, chaque employeur doit enregistrer les heures de manière numérique. Pour les travailleurs de service extérieur qui se déplacent entre sites clients, l'arrêt Tyco de l'UE (2015) signifie aussi que le déplacement du domicile au premier client et du dernier client au domicile compte comme temps de travail. L'indemnité kilométrique belge connaît des tarifs maximaux officiels (0,4449 €/km annuel, T2 2025 : 0,4320 €/km) — les dépasser exige une preuve des coûts réels. Done-it applique automatiquement le tarif indexé actuel et signale le seuil de 24.000 km/an par travailleur.",
      slug: "kilometervergoeding-in-belgie-regels-en-terugbetalingen-voor-kmo-s-en-zelfstandigen-gids-2025",
    },
    workflow: [
      {
        step: "Le technicien part pour le premier appel",
        body: "Le check-in démarre le carnet de trajets. Les kilomètres sont consignés automatiquement du départ à l'arrivée — pas de saisie manuelle.",
      },
      {
        step: "Sur le chantier",
        body: "Rattacher les heures au bon projet. Travail supplémentaire, matériel et tickets sont enregistrés dans l'app au moment où ils surviennent.",
      },
      {
        step: "Fin de mois sans le chaos",
        body: "Chaque heure, chaque kilomètre et chaque frais est déjà rattaché au bon chantier. Exportez vers le traitement salarial et facturez avec des chiffres de coût réels.",
      },
    ],
    stats: [
      { value: "0,4449 €", label: "par km — tarif officiel belge (annuel, 2025–26)" },
      { value: "24 000", label: "seuil de km/an — au-delà, les coûts réels doivent être prouvés" },
      { value: "1 janv. 2027", label: "obligation générale d'enregistrement du temps" },
    ],
    quote: {
      text: "Les kilomètres étaient autrefois un casse-tête mensuel. Maintenant ils s'enregistrent tout seuls — chaque kilomètre est rattaché au bon client avant même que nous soyons de retour au bureau.",
      who: "Responsable opérationnel, entreprise de service extérieur · 11 travailleurs",
      result: "Coût par chantier précis, à chaque fois",
    },
    relatedSlugs: ["hvac", "maintenance", "plumbing"],
    relatedArticles: [
      "kilometervergoeding-in-belgie-regels-en-terugbetalingen-voor-kmo-s-en-zelfstandigen-gids-2025",
      "facturatie-van-verplaatsingen-naar-klanten-hoe-kilometers-correct-registreren-en-aanrekenen",
      "wettelijk-en-ethisch-gebruik-van-gps-tracking-bij-mobiele-werknemers-in-belgie",
    ],
  },

  {
    slug: "hvac",
    name: "HVAC",
    headline: "Enregistrement du temps pour les techniciens HVAC — calcul du coût des chantiers, pièces et déplacement dans une seule app",
    intro:
      "Chaque appel de service comporte trois coûts : main-d'œuvre, pièces et déplacement. Done-it consigne les trois sur le terrain et les additionne au bon chantier — afin que vous connaissiez votre marge avant de clôturer la facture.",
    pains: [
      {
        title: "Les appels de service sont difficiles à chiffrer avec précision",
        body: "Main-d'œuvre, pièces et déplacement vivent dans différents systèmes — ou différentes poches. Le coût réel d'une intervention n'apparaît clairement que bien après que le technicien a quitté le chantier.",
      },
      {
        title: "Aucune preuve du temps de service",
        body: "Lorsqu'un client conteste la durée d'une réparation, un relevé d'heures papier est facile à contester. Des horodatages de check-in et de check-out confirmés par GPS, non.",
      },
      {
        title: "Les contrats d'entretien grignotent silencieusement la marge",
        body: "Avec des contrats d'entretien récurrents, de petits dépassements s'accumulent au fil de nombreuses visites. Sans un compteur d'heures en direct, vous n'apprenez qu'au renouvellement que le contrat est déficitaire.",
      },
      {
        title: "Les techniciens oublient d'enregistrer les pauses",
        body: "Les enregistrements du temps manuels ratent les pauses, ce qui entraîne un surpaiement et un traitement salarial qui ne correspond pas à la réalité.",
      },
    ],
    features: [
      {
        title: "Enregistrement du temps basé sur le projet",
        body: "Chaque appel de service ou contrat d'entretien est un projet. Heures, déplacement et matériel sont additionnés en temps réel — ce qui vous donne une vue en direct sur votre marge.",
        plan: "Project Basic",
      },
      {
        title: "Travail supplémentaire et matériel, enregistrés sur le terrain",
        body: "Lorsqu'un chantier s'étend, les techniciens enregistrent le travail supplémentaire et les pièces avec texte et photos avant de partir. Rien n'est facturé de mémoire.",
        plan: "Project Basic",
      },
      {
        title: "Kilomètres et frais de transport",
        body: "Les kilomètres par intervention sont enregistrés automatiquement et imputés au chantier. Configurez les tarifs d'indemnité par type de véhicule.",
        plan: "Add-on",
      },
      {
        title: "Déduction automatique des pauses",
        body: "Les pauses obligatoires sont automatiquement déduites du temps total — pas de discussions, pas d'erreurs de salaire.",
      },
      {
        title: "Check-in vérifié par géolocalisation et preuve de présence",
        body: "Check-in horodaté, confirmé par GPS sur chaque chantier. Preuve incontestable du moment où le technicien est arrivé et reparti.",
      },
    ],
    compliance: {
      label: "Loi de 2027",
      title: "Les équipes HVAC relèvent de l'obligation générale d'enregistrement du temps de 2027",
      body: "À partir du 1er janvier 2027, chaque employeur belge doit tenir une vue numérique et étanche des heures de travail. Pour les équipes HVAC qui travaillent sur de grands chantiers (≥ 500.000 € hors TVA), l'enregistrement CheckIn@Work est déjà requis aujourd'hui. Done-it couvre les deux scénarios et exporte les données directement vers votre secrétariat social.",
      slug: "verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedrijven-weten",
    },
    workflow: [
      {
        step: "Le technicien s'enregistre sur le chantier",
        body: "Le GPS horodate l'arrivée et rattache les heures à l'appel de service ou au contrat d'entretien. Pas de papier, pas de terminal.",
      },
      {
        step: "Le travail supplémentaire est consigné sur place",
        body: "Pièces utilisées, heures supplémentaires et modifications de la mission sont enregistrées dans l'app avec photos — avant de quitter le chantier.",
      },
      {
        step: "Le coût du chantier est immédiatement visible",
        body: "Main-d'œuvre, déplacement et matériel sont additionnés au projet en temps réel. Vous voyez la marge avant d'établir la facture.",
      },
    ],
    stats: [
      { value: "3", label: "types de coûts consignés par appel : main-d'œuvre, déplacement, pièces" },
      { value: "En direct", label: "vue sur la marge par contrat de service" },
      { value: "1 janv. 2027", label: "enregistrement numérique du temps obligatoire" },
    ],
    quote: {
      text: "Nous savons enfin ce que chaque contrat de service nous coûte réellement. Les mauvaises surprises de marge ont cessé le jour où nous avons commencé à utiliser Done-it.",
      who: "Gérant, entreprise HVAC · 7 techniciens",
      result: "Coût réel par appel de service, en direct",
    },
    relatedSlugs: ["installation", "electricians", "plumbing"],
    relatedArticles: [
      "verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedrijven-weten",
      "kilometervergoeding-in-belgie-regels-en-terugbetalingen-voor-kmo-s-en-zelfstandigen-gids-2025",
      "facturatie-van-verplaatsingen-naar-klanten-hoe-kilometers-correct-registreren-en-aanrekenen",
    ],
  },

  {
    slug: "electricians",
    name: "Électriciens",
    headline:
      "Enregistrement du temps pour les électriciens — heures de projet, gestion de budget et navigation dans une seule app",
    intro:
      "Heures de projet, décompte en direct du budget restant et navigation vers le chantier en un bouton — conçu pour les petites équipes d'électriciens qui ont besoin de gestion de projet sans la charge administrative.",
    pains: [
      {
        title: "Les heures ne sont pas rattachées au bon projet",
        body: "Lorsque des électriciens travaillent sur plusieurs chantiers dans une semaine, l'attribution manuelle des heures en fin de journée mène à des coûts mal imputés, des discussions de facturation et un traitement salarial que vous ne pouvez pas expliquer.",
      },
      {
        title: "Les dépassements de budget sont remarqués trop tard",
        body: "Sans une vue en direct des heures consommées par rapport aux heures budgétées, vous ne découvrez qu'un projet a débordé qu'au moment d'établir la facture — pas quand vous pouviez encore réagir.",
      },
      {
        title: "Du temps perdu à trouver le chantier",
        body: "Des adresses tapées dans une appli séparée, des appels au bureau pour la route, des arrivées tardives sur les sites clients. Chaque minute perdue est une minute facturable perdue.",
      },
      {
        title: "Les heures des sous-traitants difficiles à rapprocher",
        body: "Sur les installations plus grandes, les électriciens sous-traitants tiennent leurs propres heures séparément. Obtenir une vue combinée des coûts de main-d'œuvre en fin de projet est une tâche manuelle et sujette aux erreurs.",
      },
    ],
    features: [
      {
        title: "Heures de projet avec budget restant en direct",
        body: "Chaque minute travaillée est automatiquement rattachée au bon projet. Un décompte en direct montre combien d'heures il reste par rapport au budget — pour vous et pour le travailleur sur le chantier.",
        plan: "Project Basic",
      },
      {
        title: "Navigation vers le chantier en un bouton",
        body: "L'adresse du projet est dans l'app. Une pression ouvre la navigation — pas de recherche, pas d'appli de cartes séparée, pas d'appels au bureau.",
      },
      {
        title: "Check-in des travailleurs par projet",
        body: "Chaque électricien s'enregistre sur le bon chantier. Vous savez toujours qui se trouve en ce moment sur quel chantier.",
        plan: "Project Basic",
      },
      {
        title: "Travail supplémentaire enregistré sur le chantier",
        body: "Lorsque la mission change — un circuit imprévu, du matériel supplémentaire — c'est enregistré dans l'app avec photos, avant que l'équipe ne parte.",
        plan: "Project Basic",
      },
      {
        title: "Enregistrement des heures supplémentaires avec rémunération flexible",
        body: "Les heures supplémentaires sont enregistrées au moment où elles surviennent. Les travailleurs choisissent de se les faire payer ou de les convertir en repos compensatoire.",
        plan: "Time Pro",
      },
    ],
    compliance: {
      label: "Loi de 2027 + CheckIn@Work sur les grands chantiers",
      title: "Les électriciens relèvent de l'obligation de 2027 — et de CheckIn@Work là où le seuil de chantier s'applique",
      body: "À partir du 1er janvier 2027, tous les employeurs doivent enregistrer les heures de manière numérique. Pour les travaux d'électricité sur les chantiers de ≥ 500.000 € hors TVA, CheckIn@Work est déjà obligatoire aujourd'hui. Done-it couvre les deux : il fournit l'enregistrement du temps numérique qu'exige 2027 et synchronise avec CheckIn@Work lorsque le seuil de chantier est atteint.",
      slug: "verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedrijven-weten",
    },
    workflow: [
      {
        step: "L'électricien navigue vers le chantier",
        body: "Adresse et mission sont déjà dans l'app. Une pression ouvre la navigation — pas d'appels, pas de confusion.",
      },
      {
        step: "Les heures se rattachent automatiquement au bon projet",
        body: "Le check-in sur le chantier rattache chaque minute travaillée au bon projet. Le compteur d'heures restantes est mis à jour en direct.",
      },
      {
        step: "Les modifications de la mission restent enregistrées",
        body: "Travail supplémentaire et matériel sont enregistrés sur le chantier, avant le départ. La facture reflète ce qui s'est réellement passé.",
      },
    ],
    stats: [
      { value: "En direct", label: "vue sur les heures restantes par projet" },
      { value: "0", label: "appels au bureau pour les adresses de chantier" },
      { value: "1 janv. 2027", label: "enregistrement numérique du temps obligatoire" },
    ],
    quote: {
      text: "Nous savons en temps réel si un projet dépasse son budget. Cela seul nous a épargné deux mauvaises surprises cette année.",
      who: "Gérant, entreprise d'électricité · 5 électriciens",
      result: "Dépassements de budget repérés avant qu'ils ne surviennent",
    },
    relatedSlugs: ["installation", "hvac", "plumbing"],
    relatedArticles: [
      "verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedrijven-weten",
      "digitaal-in-en-uitchecken-verplicht-op-bouwwerven-vanaf-2025",
      "kilometervergoeding-in-belgie-regels-en-terugbetalingen-voor-kmo-s-en-zelfstandigen-gids-2025",
    ],
  },

  {
    slug: "plumbing",
    name: "Plombiers",
    headline:
      "Enregistrement du temps pour les équipes de plomberie — check-in rapide, traitement salarial précis et preuve de présence pour chaque intervention",
    intro:
      "Des interventions urgentes, plusieurs chantiers par jour, des heures complétées de mémoire en fin de semaine — ou pas du tout. Done-it en fait une affaire d'une seule pression sur chaque chantier et d'un traitement salarial en un clic en fin de mois.",
    pains: [
      {
        title: "Des heures d'intervention reconstituées de mémoire",
        body: "Avec plusieurs chantiers dans une journée, les plombiers ont rarement le temps de compléter des relevés d'heures précis. Les heures sont arrondies ou oubliées, et le traitement salarial est au mieux une estimation.",
      },
      {
        title: "Aucune preuve du temps sur le chantier",
        body: "Lorsqu'un client conteste la durée d'une réparation, il n'y a aucune vue objective. Un check-in numérique confirmé par GPS est facile à montrer — un relevé d'heures papier, non.",
      },
      {
        title: "Les kilomètres entre chantiers non suivis",
        body: "Les déplacements entre interventions s'accumulent sur la semaine et le mois, mais sans enregistrement automatique ils sont rarement facturés ou indemnisés correctement.",
      },
      {
        title: "La préparation des salaires est lente",
        body: "En fin de mois, courir après les relevés d'heures, corriger les erreurs et constituer l'export pour le secrétariat social prend un temps que votre équipe administrative ne devrait pas y consacrer.",
      },
    ],
    features: [
      {
        title: "Check-in vérifié par géolocalisation en une pression à chaque intervention",
        body: "Arrivée et départ enregistrés en une pression, avec vérification de la localisation du site. Assez rapide pour les appels urgents ; assez précis pour les discussions.",
        plan: "Time Basic",
      },
      {
        title: "Déduction automatique des pauses",
        body: "Les pauses obligatoires sont automatiquement déduites du temps total — rien à compléter, pas d'erreurs de salaire.",
        plan: "Time Basic",
      },
      {
        title: "Kilomètres consignés par trajet",
        body: "Configurez les types de transport et les tarifs d'indemnité. Les kilomètres sont enregistrés automatiquement et rattachés au bon client ou au bon projet.",
        plan: "Add-on",
      },
      {
        title: "Gestion des absences et des certificats médicaux",
        body: "Les travailleurs introduisent les certificats médicaux numériquement depuis l'app. Prend en charge le chômage intempéries et d'autres types d'absence pertinents pour la plomberie en extérieur.",
      },
      {
        title: "Export salarial en un clic",
        body: "Toutes les données d'heures et de kilomètres, vous les exportez proprement pour votre secrétariat social — pas de préparation manuelle, pas d'Excel.",
        plan: "Time Basic",
      },
    ],
    compliance: {
      label: "Loi de 2027",
      title: "La plomberie relève de l'obligation générale d'enregistrement du temps de 2027",
      body: "À partir du 1er janvier 2027, chaque employeur belge — y compris les petites équipes de plomberie — doit tenir une vue numérique, précise et infalsifiable des heures de travail. Les relevés d'heures papier et les tableurs partagés ne suffisent plus. Done-it couvre l'exigence d'emblée, exporte vers votre secrétariat social et garde tous les enregistrements conformes au RGPD.",
      slug: "verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedrijven-weten",
    },
    workflow: [
      {
        step: "Le plombier arrive à l'intervention",
        body: "Une pression enregistre l'entrée. Le GPS enregistre la localisation et l'heure. Assez rapide pour un chantier urgent.",
      },
      {
        step: "Départ tapé en sortant",
        body: "Le check-out clôture l'enregistrement du temps. Les kilomètres vers le chantier suivant commencent à s'enregistrer automatiquement.",
      },
      {
        step: "La fin de mois tient en un clic",
        body: "Toutes les interventions, heures et kilomètres, vous les exportez proprement. La préparation des salaires passe d'une journée à quelques minutes.",
      },
    ],
    stats: [
      { value: "1 pression", label: "pour s'enregistrer — assez rapide pour les appels urgents" },
      { value: "1 clic", label: "export salarial vers votre secrétariat social" },
      { value: "1 janv. 2027", label: "enregistrement numérique du temps obligatoire" },
    ],
    quote: {
      text: "Nous perdions autrefois une demi-matinée en fin de mois à reconstituer les heures. Maintenant nous exportons et envoyons, tout simplement. Le secrétariat social le reçoit proprement.",
      who: "Gérant, entreprise de plomberie · 4 plombiers",
      result: "Préparation des salaires : d'heures à minutes",
    },
    relatedSlugs: ["hvac", "electricians", "field-service"],
    relatedArticles: [
      "verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedrijven-weten",
      "kilometervergoeding-in-belgie-regels-en-terugbetalingen-voor-kmo-s-en-zelfstandigen-gids-2025",
      "facturatie-van-verplaatsingen-naar-klanten-hoe-kilometers-correct-registreren-en-aanrekenen",
    ],
  },

  {
    slug: "landscaping",
    name: "Aménagement paysager",
    headline:
      "Enregistrement du temps pour les entreprises d'aménagement paysager — équipes saisonnières, chantiers multiples et kilomètres dans une seule vue",
    intro:
      "La haute saison, ce sont beaucoup de chantiers, des équipes changeantes et pas mal de kilomètres. Done-it garde chaque heure, chaque absence et chaque trajet organisés — pour que vous puissiez vous concentrer sur le travail, pas sur la paperasse.",
    pains: [
      {
        title: "Les équipes saisonnières sont complexes à gérer",
        body: "Les travailleurs temporaires, les horaires changeants et les tailles d'équipe variables rendent l'enregistrement précis des heures presque impossible sur papier. Au moment où vous faites le rapprochement en fin de mois, il manque toujours quelque chose.",
      },
      {
        title: "Des heures dispersées sur de nombreux chantiers",
        body: "Une équipe qui visite trois ou quatre chantiers par jour génère des heures qui doivent être réparties correctement. La saisie manuelle mène à des mauvaises imputations et un traitement salarial contesté.",
      },
      {
        title: "Le chômage intempéries est difficile à documenter",
        body: "Le chômage intempéries est une réalité dans l'aménagement paysager. Sans une vue numérique, demander l'indemnité correcte et gérer l'eC3.2A est lent et sujet aux erreurs.",
      },
      {
        title: "Les kilomètres entre chantiers restent non enregistrés",
        body: "Camionnettes de société, véhicules personnels et covoiturages roulent toute la journée entre chantiers. Sans enregistrement automatique, ces kilomètres n'atterrissent jamais dans le calcul des coûts.",
      },
    ],
    features: [
      {
        title: "Vue d'équipe sur plusieurs chantiers",
        body: "Voyez toutes les équipes, tous les chantiers en temps réel. Sachez qui travaille où, en ce moment — pas en fin de mois.",
      },
      {
        title: "Planification des shifts pour les équipes saisonnières",
        body: "Planifiez qui travaille où, gérez les changements de dernière minute et envoyez le planning vers les téléphones de l'équipe — sans un appel à chaque personne.",
        plan: "Time Pro",
      },
      {
        title: "Types d'absence personnalisés",
        body: "Créez des types d'absence personnalisés pour votre secteur : chômage intempéries saisonnier, congé parental, jours de maladie. Les travailleurs chargent la documentation numériquement depuis l'app.",
      },
      {
        title: "Kilomètres avec enregistrement du covoiturage",
        body: "Configurez les types de transport (camionnette de société, voiture personnelle, covoiturage conducteur vs. passager) avec leurs propres tarifs. Les kilomètres sont enregistrés automatiquement par trajet.",
        plan: "Add-on",
      },
      {
        title: "Enregistrement des heures supplémentaires avec conversion en congé",
        body: "Les heures supplémentaires en haute saison s'accumulent vite. Chaque travailleur peut choisir de se les faire payer ou d'épargner les heures en repos compensatoire — suivi de façon transparente.",
        plan: "Time Pro",
      },
    ],
    compliance: {
      label: "Loi de 2027 + chômage intempéries",
      title: "L'aménagement paysager relève de l'obligation de 2027 — et le chômage intempéries ajoute une obligation numérique spécifique",
      body: "À partir du 1er janvier 2027, chaque employeur doit enregistrer les heures de manière numérique. Les équipes d'aménagement paysager qui travaillent en extérieur sont aussi souvent confrontées au chômage intempéries (chômage temporaire pour cause d'intempéries). Depuis le 1er janvier 2025, la carte de contrôle numérique eC3.2A est obligatoire pour le chômage temporaire dans les secteurs incluant le travail en extérieur. Done-it prend en charge des types d'absence personnalisés pour le chômage intempéries et fournit l'enregistrement du temps numérique qu'exige la loi de 2027.",
      slug: "verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedrijven-weten",
    },
    workflow: [
      {
        step: "L'équipe s'enregistre sur le premier chantier",
        body: "Le GPS enregistre l'arrivée. Les heures sont automatiquement réparties sur le bon site et le bon projet — pas d'attribution manuelle en fin de journée.",
      },
      {
        step: "Le temps met le travail à l'arrêt",
        body: "Le chef d'équipe enregistre le chômage intempéries depuis l'app. La vue numérique soutient la procédure eC3.2A sans paperasse supplémentaire.",
      },
      {
        step: "Fin de mois : tous les chantiers, toutes les équipes, un seul export",
        body: "Heures, absences et kilomètres sur chaque chantier et chaque membre d'équipe, vous les exportez proprement pour le traitement salarial.",
      },
    ],
    stats: [
      { value: "1 janv. 2025", label: "carte numérique eC3.2A obligatoire pour le chômage temporaire" },
      { value: "1 janv. 2027", label: "obligation générale d'enregistrement du temps" },
      { value: "Automatique", label: "enregistrement des kilomètres — fini le carnet de route" },
    ],
    quote: {
      text: "La haute saison rimait autrefois avec chaos salarial. Maintenant notre chef d'équipe consulte l'app, tout le monde est enregistré, et j'exporte les heures le dernier jour du mois.",
      who: "Gérant, entreprise d'aménagement paysager · 12 travailleurs saisonniers",
      result: "Fini la reconstruction du traitement salarial en fin de mois",
    },
    relatedSlugs: ["maintenance", "construction", "field-service"],
    relatedArticles: [
      "verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedrijven-weten",
      "wettelijk-en-ethisch-gebruik-van-gps-tracking-bij-mobiele-werknemers-in-belgie",
      "kilometervergoeding-in-belgie-regels-en-terugbetalingen-voor-kmo-s-en-zelfstandigen-gids-2025",
    ],
  },

  {
    slug: "maintenance",
    name: "Entretien",
    headline:
      "Enregistrement du temps pour les équipes d'entretien — visites de chantier récurrentes, coût par contrat et preuve de présence dans une seule app",
    intro:
      "Les contrats d'entretien récurrents tiennent ou tombent avec la vue sur les coûts. Done-it enregistre chaque visite, suit les heures par rapport au budget du contrat et vous donne — à vous et à vos clients — une preuve de présence pour chaque appel.",
    pains: [
      {
        title: "Aucune vue sur les coûts par contrat d'entretien",
        body: "Lorsque les heures des visites récurrentes ne sont pas suivies par rapport au contrat spécifique, vous ne savez qu'au décompte annuel si un client est rentable — et il est alors trop tard pour reprixer.",
      },
      {
        title: "Les clients attendent une preuve de ce qui a été fait",
        body: "Les facility managers et les propriétaires veulent de plus en plus des enregistrements de présence : qui est venu, quand ils sont arrivés, ce qu'ils ont fait. Un journal papier présenté des semaines plus tard ne satisfait pas aux exigences de SLA.",
      },
      {
        title: "Les visites récurrentes difficiles à planifier et à enregistrer",
        body: "Des shifts changeants sur de nombreux chantiers récurrents — avec du personnel qui remplace les uns les autres — créent un casse-tête de planification et d'enregistrement des heures que les processus manuels ne peuvent pas résoudre de façon fiable.",
      },
      {
        title: "Le chômage intempéries touche aussi l'entretien extérieur",
        body: "L'entretien des jardins, l'entretien des façades et le travail sur les installations extérieures sont tous confrontés à l'arrêt lié au temps. Documenter correctement le chômage intempéries exige une trace numérique.",
      },
    ],
    features: [
      {
        title: "Enregistrement des coûts basé sur le contrat",
        body: "Chaque contrat d'entretien est un projet. Les heures y sont automatiquement enregistrées, ce qui vous donne un chiffre de coût en direct et un décompte des heures restantes par client.",
        plan: "Project Basic",
      },
      {
        title: "Preuve de présence confirmée par GPS",
        body: "Des check-ins horodatés, vérifiés par géolocalisation, constituent une vue incontestable de qui était sur le chantier, quand et combien de temps — prête à être partagée avec les clients sur demande.",
      },
      {
        title: "Mises à jour avec photos depuis le chantier",
        body: "Les travailleurs signalent des problèmes, des tâches achevées ou du travail supplémentaire directement depuis l'app — avec texte et photos. Les clients reçoivent une vue en temps réel ; vous avez la documentation pour la facturation.",
        plan: "Project Basic",
      },
      {
        title: "Planification des shifts récurrents",
        body: "Planifiez les visites récurrentes sur tous les contrats, attribuez les travailleurs et gérez les remplacements — sans un appel à chaque personne.",
        plan: "Time Pro",
      },
      {
        title: "Types d'absence pour l'entretien extérieur",
        body: "Configurez le chômage intempéries et d'autres types d'absence. Les travailleurs enregistrent l'arrêt depuis l'app ; la documentation est chargée numériquement.",
      },
    ],
    compliance: {
      label: "Loi de 2027 + chômage intempéries",
      title: "Les équipes d'entretien relèvent de l'obligation de 2027 et sont confrontées à des obligations numériques spécifiques au chômage intempéries",
      body: "À partir du 1er janvier 2027, chaque employeur doit tenir une vue numérique des heures de travail. Pour le travail d'entretien extérieur, le chômage intempéries (chômage temporaire pour cause d'intempéries) ajoute une obligation spécifique : depuis le 1er janvier 2025, la carte de contrôle eC3.2A sous forme numérique est obligatoire. Done-it couvre les deux : il fournit l'enregistrement général du temps qu'exige 2027 et prend en charge des types d'absence personnalisés — y compris le chômage intempéries — avec documentation numérique.",
      slug: "verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedrijven-weten",
    },
    workflow: [
      {
        step: "Le travailleur arrive sur le chantier d'entretien",
        body: "Le check-in vérifié par géolocalisation enregistre l'heure d'arrivée et rattache les heures au bon contrat. La preuve de présence est créée automatiquement.",
      },
      {
        step: "Tâches achevées, problèmes enregistrés",
        body: "Le travail achevé et les éventuels problèmes sont signalés depuis l'app avec photos. Les clients voient précisément ce qui a été fait.",
      },
      {
        step: "Coût par contrat immédiatement visible",
        body: "Les heures s'accumulent en temps réel par rapport au budget du contrat. Vous connaissez la marge avant que le contrat ne soit renouvelé.",
      },
    ],
    stats: [
      { value: "En direct", label: "coût par contrat d'entretien" },
      { value: "GPS", label: "preuve de présence prête pour les clients" },
      { value: "1 janv. 2027", label: "enregistrement numérique du temps obligatoire" },
    ],
    quote: {
      text: "Nous pouvons dire à un facility manager en deux minutes qui était exactement sur le chantier et quand. Ce niveau de preuve a changé la façon dont les clients nous perçoivent.",
      who: "Responsable opérationnel, entreprise d'entretien · 8 travailleurs",
      result: "Discussions clients réduites à zéro",
    },
    relatedSlugs: ["cleaning", "landscaping", "field-service"],
    relatedArticles: [
      "verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedrijven-weten",
      "wettelijk-en-ethisch-gebruik-van-gps-tracking-bij-mobiele-werknemers-in-belgie",
      "checkin-work-ciao-verplichtingen-fouten-en-tips-voor-kmo-s-in-belgie",
    ],
  },
  {
    slug: "retail",
    name: "Commerces & retail",
    headline: "Pointage pour les commerces — shifts, remplacements et export prêt pour la paie, dans une seule app",
    intro: "Shifts d'ouverture, shifts tardifs et toujours quelqu'un qui remplace quelqu'un. Done-it donne à chaque magasin un relevé numérique clair de qui a presté quel shift — pointé depuis le téléphone de chacun, prêt pour la paie en fin de mois.",
    pains: [
      {
        title: "Les échanges de shifts n'arrivent jamais à la paie",
        body: "Une collègue malade remplacée, quelqu'un qui reste une heure de plus à la fermeture. Ça finit sur un post-it, ou nulle part — et la paie est déjà fausse avant même de commencer.",
      },
      {
        title: "Plusieurs magasins, une seule paie",
        body: "Les collaborateurs circulent entre les points de vente, les temps partiels prestent sur deux adresses. Rassembler les heures par magasin et les fusionner à la main coûte une journée par mois.",
      },
      {
        title: "Pauses et heures supplémentaires au jugé",
        body: "Les pauses légales sont déduites de mémoire et les heures en plus se règlent de manière informelle — exactement les deux points qu'un contrôle examine en premier.",
      },
    ],
    features: [
      {
        title: "Pointer depuis la surface de vente",
        body: "Un tap sur son propre téléphone au début du shift. Pas de terminal à la caisse, pas de badge partagé, pas de feuille accrochée près de la réserve.",
      },
      {
        title: "Les heures par magasin, l'équipe entre les points de vente",
        body: "Chaque magasin est un projet. Un renfort dans un autre point de vente est pointé sur ce point de vente — vous voyez les heures là où elles ont réellement été prestées.",
      },
      {
        title: "Pauses déduites automatiquement",
        body: "Vous définissez la règle de pause une fois, elle s'applique à chaque shift. Les heures supplémentaires sont enregistrées dès qu'elles tombent, et chaque collaborateur choisit le paiement ou la récupération.",
      },
    ],
    compliance: {
      label: "Enregistrement du temps par shift",
      title: "Un relevé défendable de chaque shift presté",
      body: "Done-it conserve un relevé numérique objectif de chaque pointage d'entrée et de sortie, pauses et heures supplémentaires comprises — le relevé qu'un contrôle ou un litige de paie réclame.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "L'équipe pointe pour son shift", body: "Un tap sur le téléphone au début du shift. Un renfort dans un autre point de vente est pointé sur ce point de vente." },
      { step: "Vous voyez qui est présent, par magasin", body: "Une vue en direct sur tous les points de vente — repérez un shift d'ouverture non couvert avant que les portes s'ouvrent." },
      { step: "La paie en un seul export", body: "Heures, pauses et heures supplémentaires par collaborateur et par magasin, exportées telles quelles pour votre secrétariat social." },
    ],
    stats: [
      { value: "1 tap", label: "pour pointer — sans terminal à la caisse" },
      { value: "Live", label: "qui est présent, dans chaque magasin" },
      { value: "0", label: "remplacement perdu entre le magasin et la paie" },
    ],
    quote: {
      text: "Deux magasins et beaucoup de temps partiels. Avant, la moitié des remplacements se réglait de mémoire. Aujourd'hui chaque heure est sur le bon magasin et la paie prend quelques minutes.",
      who: "Gérante, commerce · 11 collaborateurs",
      result: "Préparation de la paie : d'une journée à moins d'une heure",
    },
    relatedSlugs: ["hospitality", "beauty", "offices"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "offices",
    name: "Bureaux & services professionnels",
    headline: "Pointage pour les bureaux — les heures par client et par projet, sans tableur",
    intro: "Comptables, notaires, architectes, agences et bureaux de conseil facturent leur temps — ou doivent au moins savoir où il est passé. Done-it enregistre les heures sur le bon client et le bon projet, depuis le bureau comme en déplacement.",
    pains: [
      {
        title: "Des heures facturables reconstituées le vendredi",
        body: "Un temps noté en fin de semaine est un temps estimé. Des heures entières disparaissent discrètement entre la prestation et la facture.",
      },
      {
        title: "Aucune idée de ce que coûte réellement un client",
        body: "Les honoraires ont été convenus il y a des mois. Savoir si le travail y rentre encore n'apparaît que quand quelqu'un fait le total — souvent trop tard.",
      },
      {
        title: "Le travail hybride rend le relevé lacunaire",
        body: "Une partie de l'équipe au bureau, une partie à domicile, une partie chez le client. La présence n'est plus un fait, elle devient une reconstitution.",
      },
    ],
    features: [
      {
        title: "Des heures liées au client et au projet",
        body: "Chaque saisie tombe sur un projet : vous voyez les heures par client sans rien exporter ni demander à personne.",
      },
      {
        title: "Au bureau comme à l'extérieur",
        body: "La même app sur le téléphone ou dans le navigateur. Une visite chez un client compte comme du travail sur ce client, déplacement inclus si vous le souhaitez.",
      },
      {
        title: "Heures supplémentaires et congés au même endroit",
        body: "Les heures en plus enregistrées quand elles tombent, les congés demandés et approuvés dans l'app — plus de tableur parallèle pour savoir qui est absent quand.",
      },
    ],
    compliance: {
      label: "Enregistrement du temps par client",
      title: "Un relevé objectif des heures prestées",
      body: "Done-it conserve un relevé numérique et infalsifiable du temps de travail par collaborateur — la base d'une paie correcte comme d'une facture client défendable.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Ouvrez un projet client", body: "Configurez-le une fois avec le client et, si vous facturez au budget, les heures prévues." },
      { step: "L'équipe y impute son temps", body: "Depuis le bureau ou le téléphone. Chaque heure tombe sur un client, pas dans la mémoire de quelqu'un." },
      { step: "Voyez les heures par client, exportez pour la paie", body: "Des totaux en direct par projet et un export propre pour la paie ou la facturation en fin de mois." },
    ],
    stats: [
      { value: "Par client", label: "les heures, visibles pendant que le dossier tourne" },
      { value: "Bureau ou route", label: "le même relevé, téléphone et navigateur" },
      { value: "1 export", label: "pour la paie et la facturation" },
    ],
    quote: {
      text: "On reconstituait la semaine chaque vendredi. Maintenant les heures sont déjà sur le bon client et je vois quels dossiers mangent les honoraires.",
      who: "Associé, cabinet comptable · 8 collaborateurs",
      result: "Des heures facturables récupérées chaque mois",
    },
    relatedSlugs: ["retail", "healthcare", "wellness"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "beauty",
    name: "Salons & soins du corps",
    headline: "Pointage pour les salons — shifts, temps partiels et paie sans agenda papier",
    intro: "Coiffeurs, barbiers, onglerie et instituts de beauté tournent avec des shifts et des contrats à temps partiel. Done-it enregistre qui a presté quand, déduit les pauses automatiquement et remet à la paie un fichier propre.",
    pains: [
      {
        title: "Temps partiels, variations sans fin",
        body: "Trois jours une semaine, quatre la suivante, un samedi échangé contre un mardi. Heures contractuelles et heures prestées s'écartent vite.",
      },
      {
        title: "L'agenda n'est pas un relevé du temps de travail",
        body: "Les rendez-vous disent ce qui a été réservé, pas qui a presté. Quand la paie ou un contrôle pose la question, le logiciel de réservation est la mauvaise réponse.",
      },
      {
        title: "Des heures en plus réglées à l'amiable",
        body: "Rester plus tard pour une dernière cliente : on s'en souvient, on le promet, et parfois on l'oublie. C'est le genre d'écart qui finit en litige.",
      },
    ],
    features: [
      {
        title: "Pointer au salon",
        body: "Un tap sur son propre téléphone au début du shift — pas de terminal partagé à l'accueil, pas de liste de signatures près du miroir.",
      },
      {
        title: "Pauses et règles de temps partiel, une seule fois",
        body: "Définissez la règle de pause et les heures standard une fois ; elles s'appliquent à chaque shift, avec des exceptions par personne là où un contrat diffère.",
      },
      {
        title: "Heures supplémentaires : payées ou récupérées",
        body: "Les heures en plus sont enregistrées au moment où elles sont prestées, et chaque collaborateur choisit le paiement ou la récupération — réglé dans l'app, pas de mémoire.",
      },
    ],
    compliance: {
      label: "Enregistrement numérique du temps",
      title: "Un relevé qui tient au-delà du carnet de rendez-vous",
      body: "Done-it enregistre le temps de travail réel par collaborateur, pauses et heures supplémentaires comprises — distinct des réservations, et défendable le jour du contrôle.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "L'équipe pointe pour son shift", body: "Un tap à l'arrivée. Les pauses sont déduites selon la règle que vous définissez une fois." },
      { step: "Vous suivez la journée en direct", body: "Qui est présent, qui est en pause, qui est resté plus tard — en direct, sans devoir demander." },
      { step: "La paie reçoit un fichier propre", body: "Heures prestées, pauses et heures supplémentaires par collaborateur, exportées pour votre secrétariat social." },
    ],
    stats: [
      { value: "1 tap", label: "pour pointer — rien de partagé, rien sur papier" },
      { value: "Auto", label: "déduction des pauses à chaque shift" },
      { value: "0", label: "heure tardive réglée de mémoire" },
    ],
    quote: {
      text: "Tout le monde est à temps partiel et chaque semaine est différente. L'app enregistre, tout simplement — j'ai arrêté de reconstruire le mois dans un tableur.",
      who: "Gérante, salon de coiffure · 6 collaborateurs",
      result: "Plus d'heures contestées en fin de mois",
    },
    relatedSlugs: ["wellness", "retail", "hospitality"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "wellness",
    name: "Santé & bien-être",
    headline: "Pointage pour salles de sport, studios et spas — shifts, cours et paie dans une seule app",
    intro: "Salles de sport, studios de fitness, cabinets de kiné et spas ouvrent longtemps avec des équipes qui tournent. Done-it enregistre qui a couvert quel shift, déduit les pauses automatiquement et exporte la paie proprement.",
    pains: [
      {
        title: "Longues heures d'ouverture, remplacements permanents",
        body: "Shifts du matin, cours du soir et week-ends, couverts par un mélange de temps plein, de temps partiels et d'indépendants. Le planning est une intention, pas un relevé.",
      },
      {
        title: "Heures de cours et heures de salle se confondent",
        body: "Un coach donne deux cours et tient l'accueil entre les deux. Le payer correctement suppose de savoir ce qui a été presté, pas ce qui était planifié.",
      },
      {
        title: "Plusieurs sites, une seule administration",
        body: "Les collaborateurs passent d'un studio à l'autre. Fusionner leurs heures à la main chaque mois est lent et source d'erreurs.",
      },
    ],
    features: [
      {
        title: "Pointer par studio",
        body: "Un tap sur le téléphone, sur le site où l'on preste. Un remplacement dans un autre studio tombe sur les heures de ce studio.",
      },
      {
        title: "Vue en direct des présences",
        body: "Voyez la salle sur tous les sites en temps réel — un shift du matin non couvert se voit avant l'ouverture, pas après.",
      },
      {
        title: "Pauses et heures supplémentaires gérées",
        body: "Règles de pause appliquées automatiquement, heures en plus enregistrées dès qu'elles tombent, avec paiement ou récupération au choix de chaque collaborateur.",
      },
    ],
    compliance: {
      label: "Enregistrement des shifts multi-sites",
      title: "Un relevé objectif pour chaque shift et chaque site",
      body: "Done-it conserve un relevé numérique du temps de travail par collaborateur et par site — la base d'une rémunération correcte et de tout contrôle des heures prestées.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "L'équipe pointe au studio", body: "Un tap au début du shift, sur le site où elle preste." },
      { step: "Les remplacements se voient en direct", body: "Qui est présent, sur quel site, à cet instant — les trous se comblent avant que les membres le remarquent." },
      { step: "Un seul export pour la paie", body: "Heures, pauses et heures supplémentaires par collaborateur et par studio, prêtes pour le secrétariat social." },
    ],
    stats: [
      { value: "Par studio", label: "les heures, même en renfort ailleurs" },
      { value: "Live", label: "la salle en vue, sur tous les sites" },
      { value: "Auto", label: "pauses et heures sup, sans calcul manuel" },
    ],
    quote: {
      text: "Deux studios, de longues heures d'ouverture et beaucoup de remplacements. Aujourd'hui les heures tombent sur le bon site et la paie tient dans un fichier.",
      who: "Manager, studio de fitness · 14 collaborateurs",
      result: "Le planning et la paie concordent enfin",
    },
    relatedSlugs: ["beauty", "healthcare", "hospitality"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "hospitality",
    name: "Horeca",
    headline: "Pointage pour cafés, restaurants et hôtels — shifts, pauses et heures supplémentaires réglés équitablement",
    intro: "Shifts coupés, fermetures tardives et un samedi chargé qui déborde. Done-it enregistre les heures telles qu'elles sont réellement prestées, déduit les pauses selon votre règle et remet à la paie un seul fichier propre.",
    pains: [
      {
        title: "Le shift ne finit jamais à l'heure du planning",
        body: "Le service traîne, la fermeture prend encore une demi-heure. Ces minutes sont du vrai travail, et les régler de mémoire est le point de départ des litiges.",
      },
      {
        title: "Les shifts coupés sont difficiles à enregistrer",
        body: "Service du midi, une coupure, puis le soir. Sur papier, ça devient un seul long bloc ou deux estimations — ni l'un ni l'autre n'est la réalité.",
      },
      {
        title: "Fort turnover, onboarding permanent",
        body: "De nouveaux collaborateurs à chaque saison. Un système qui demande une formation ne survivra pas à la première semaine chargée.",
      },
    ],
    features: [
      {
        title: "Pointer à l'entrée et à la sortie de chaque service",
        body: "Un tap au début et à la fin de chaque bloc : les shifts coupés sont enregistrés tels qu'ils se sont déroulés, pas fusionnés en un seul.",
      },
      {
        title: "Les pauses selon votre règle",
        body: "Pauses obligatoires déduites automatiquement, avec des exceptions par personne là où un contrat l'exige. Aucun calcul manuel en fin de mois.",
      },
      {
        title: "Heures supplémentaires réglées dans l'app",
        body: "La demi-heure après la fermeture est enregistrée au moment où elle est prestée, puis payée ou récupérée en congé — le collaborateur choisit.",
      },
    ],
    compliance: {
      label: "Enregistrement des shifts coupés",
      title: "Un relevé défendable dans une équipe à fort turnover",
      body: "Done-it conserve un relevé numérique objectif de chaque shift, pause et heure en plus — exactement ce qu'un contrôle de la paie ou une inspection sociale demande à voir.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "L'équipe pointe pour le service", body: "Un tap au début du bloc, un autre à la fin. Les shifts coupés restent coupés." },
      { step: "Vous voyez la salle en direct", body: "Qui est là pour le service du soir, qui est encore en pause, qui est resté après la fermeture." },
      { step: "La paie reçoit les heures réelles", body: "Temps presté, pauses et heures supplémentaires par collaborateur, exportés en un seul fichier." },
    ],
    stats: [
      { value: "Shifts coupés", label: "enregistrés en deux blocs, pas en une estimation" },
      { value: "Auto", label: "déduction des pauses selon votre règle" },
      { value: "1 min", label: "pour lancer un nouveau — c'est son propre téléphone" },
    ],
    quote: {
      text: "La fermeture déborde toujours. Aujourd'hui ces minutes sont enregistrées au lieu d'être discutées, et l'équipe peut les récupérer en congé.",
      who: "Patron, restaurant · 17 collaborateurs",
      result: "Moins de litiges, une paie plus rapide",
    },
    relatedSlugs: ["retail", "wellness", "beauty"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "healthcare",
    name: "Santé & soins",
    headline: "Pointage pour les équipes de soins — shifts, visites à domicile et qualifications, dans un seul relevé",
    intro: "Cabinets, équipes de soins à domicile et maisons de repos fonctionnent au rythme des shifts, des visites et d'une tenue de dossier stricte. Done-it enregistre le temps presté par shift et par visite, et garde les documents des collaborateurs là où vous les retrouvez.",
    pains: [
      {
        title: "Visites et shifts dans des systèmes séparés",
        body: "Les visites à domicile vivent dans un outil, les heures planifiées dans un autre, et la paie reçoit une troisième version. Les rapprocher est un travail mensuel.",
      },
      {
        title: "Heures de nuit et de week-end mal valorisées",
        body: "Les heures inconfortables ont d'autres barèmes. Quand le relevé est approximatif, la rémunération l'est aussi — et la correction arrive plus tard.",
      },
      {
        title: "Les certificats expirent en silence",
        body: "Les qualifications et les certificats comptent dans les soins, et ils dorment dans un classeur que personne n'ouvre jusqu'au jour du contrôle.",
      },
    ],
    features: [
      {
        title: "Pointer par shift ou par visite",
        body: "Un tap au début du shift, ou à chaque adresse de patient pour les soins à domicile — avec vérification du lieu là où la preuve est nécessaire.",
      },
      {
        title: "Des heures ventilées comme vous les payez",
        body: "Heures de nuit, de week-end et normales enregistrées distinctement : l'export de paie reflète déjà la manière dont le travail est réellement rémunéré.",
      },
      {
        title: "Les documents des collaborateurs au même endroit",
        body: "Contrats, certificats et pièces d'identité rangés à côté de la personne concernée — retrouvés en quelques secondes quand on les demande.",
      },
    ],
    compliance: {
      label: "Enregistrement des shifts & visites",
      title: "Des relevés qui tiennent face à une inspection",
      body: "Done-it conserve un relevé numérique et objectif du temps de travail par collaborateur — heures, pauses et ventilation des heures inconfortables — à côté des documents propres à chaque personne.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Pointer pour le shift ou la visite", body: "Un tap sur le site ou à l'adresse du patient, avec vérification du lieu là où une preuve est nécessaire." },
      { step: "Les heures tombent dans la bonne catégorie", body: "Heures normales, de nuit et de week-end enregistrées distinctement, au fil de la prestation." },
      { step: "Export pour la paie", body: "Un fichier propre par période, déjà ventilé comme vous le payez." },
    ],
    stats: [
      { value: "Par visite", label: "ou par shift — selon le rythme de votre équipe" },
      { value: "Ventilées", label: "heures de nuit et de week-end, telles que prestées" },
      { value: "Classés", label: "certificats conservés par collaborateur" },
    ],
    quote: {
      text: "Les visites à domicile et les shifts planifiés sont enfin dans le même relevé. La paie a cessé d'être un exercice de rapprochement.",
      who: "Coordinatrice, équipe de soins à domicile · 22 collaborateurs",
      result: "Un seul relevé au lieu de trois",
    },
    relatedSlugs: ["wellness", "offices", "cleaning"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },
];
